import {
  PARAMETRI_PREDEFINITI,
  TARATURA_NEUTRA,
  type Canale,
  type Generatore,
  type Impianto,
  type Modulo,
  type Sorgente,
  type TipoGeneratore,
  type Zona,
} from '@emacomfort/domain';

/**
 * Un impianto COMPOSTO da una configurazione, invece che scelto fra tre
 * fixture fisse.
 *
 * Le fixture `cstai` ed `ecobel` restano quello che sono: due installazioni
 * reali, riprodotte fedelmente, e non vanno parametrizzate — servono proprio
 * perché sono *quelle*. Ma provare l'app su una configurazione che non è
 * ancora stata installata da nessuno (sei zone senza sanitario, terze parti
 * senza fotovoltaico, quattro generatori in cascata) significava finora
 * scrivere una quarta fixture a mano.
 *
 * Questo modulo copre quel caso: una configurazione dichiarativa → un
 * `Impianto` valido, con i canali coerenti con ciò che la configurazione dice
 * di avere. La regola che tiene insieme il tutto è che **un canale esiste solo
 * se esiste la cosa che misura**: niente sonda esterna significa nessun canale
 * `TEMPERATURA_ESTERNA`, e l'interfaccia mostrerà quel dato come assente
 * invece che come "— —" senza spiegazione. È la stessa disciplina del puffer
 * senza sonda nella fixture CSTAI (§5.8).
 *
 * La configurazione viaggia nell'URL (`daQuery` / `aQuery`), così una
 * configurazione da riprodurre è un link e non una descrizione a parole.
 */

export type ModoCentralina = 'emacomfort' | 'terze_parti';
export type ModoFotovoltaico = 'assente' | 'senza_batteria' | 'con_batteria';

export interface ConfigurazioneSimulata {
  /** Centralina propria (comandabile) o sistema di terzi letto da un connettore (sola lettura). */
  centralina: ModoCentralina;
  /** Numero di zone, 1–8. */
  zone: number;
  /** Acqua calda sanitaria con sonda di accumulo. */
  sanitario: boolean;
  /** Ventilazione meccanica controllata. */
  vmc: boolean;
  fotovoltaico: ModoFotovoltaico;
  /** Generatori in cascata, in ordine di priorità (max 4, §MAX_GENERATORI). */
  generatori: TipoGeneratore[];
  sondaEsterna: boolean;
  umidita: boolean;
  /**
   * Le sonde di centrale termica (mandata e ritorno del collettore).
   *
   * Esistono impianti che non le hanno affatto: un connettore verso termostati
   * di marca pubblica le temperature delle stanze e nient'altro — nessuna
   * mandata, nessun ritorno, nessun delta T. E' la forma della casa letta via
   * Hive (ADR-060), ed e' una configurazione che va potuta provare, perche' e'
   * quella in cui l'interfaccia rischia di mostrare quattro trattini al posto
   * di una spiegazione.
   */
  sondeCentrale: boolean;
  /** Il puffer può esserci senza sonda: è il caso reale CSTAI. */
  sondaPuffer: boolean;
}

export const CONFIGURAZIONE_PREDEFINITA: ConfigurazioneSimulata = {
  centralina: 'emacomfort',
  zone: 4,
  sanitario: true,
  vmc: true,
  fotovoltaico: 'assente',
  generatori: ['POMPA_DI_CALORE', 'CALDAIA_GAS'],
  sondaEsterna: true,
  umidita: true,
  sondeCentrale: true,
  sondaPuffer: false,
};

export const MAX_ZONE = 8;

/** Le stanze da cui si pescano le zone, in ordine: la prima zona è il soggiorno. */
const STANZE: Array<{ id: string; nome: string; icona: Zona['icona']; setpoint: number }> = [
  { id: 'soggiorno', nome: 'Soggiorno', icona: 'soggiorno', setpoint: 21 },
  { id: 'cucina', nome: 'Cucina', icona: 'cucina', setpoint: 20.5 },
  { id: 'camera', nome: 'Camera', icona: 'camera', setpoint: 19.5 },
  { id: 'bagno', nome: 'Bagno', icona: 'bagno', setpoint: 22 },
  { id: 'cameretta', nome: 'Cameretta', icona: 'cameretta', setpoint: 20 },
  { id: 'studio', nome: 'Studio', icona: 'studio', setpoint: 20 },
  { id: 'taverna', nome: 'Taverna', icona: 'taverna', setpoint: 18 },
  { id: 'veranda', nome: 'Veranda', icona: 'veranda', setpoint: 18 },
];

const NOMI_GENERATORE: Record<TipoGeneratore, string> = {
  POMPA_DI_CALORE: 'Pompa di calore',
  CALDAIA_GAS: 'Caldaia a gas',
  STUFA_PELLET: 'Stufa a pellet',
  TERMOCAMINO: 'Termocamino',
};

/** Un generatore modula solo se il tipo lo consente: un termocamino non modula. */
const MODULANTE: Record<TipoGeneratore, boolean> = {
  POMPA_DI_CALORE: true,
  CALDAIA_GAS: true,
  STUFA_PELLET: false,
  TERMOCAMINO: false,
};

function sonda(id: string, moduloId: string, assegnazione: Canale['assegnazione'], extra: Partial<Canale> = {}): Canale {
  return {
    id,
    moduloId,
    direzione: 'INPUT',
    grandezza: 'TEMPERATURA',
    unita: '°C',
    tipoSonda: 'NTC_10K',
    taratura: { ...TARATURA_NEUTRA },
    assegnazione,
    confidenza: 'alta',
    ...extra,
  };
}

function uscita(id: string, moduloId: string, assegnazione: Canale['assegnazione'], grandezza: Canale['grandezza'] = 'COMANDO'): Canale {
  return {
    id,
    moduloId,
    direzione: 'OUTPUT',
    grandezza,
    unita: grandezza === 'MODULAZIONE' ? '%' : '',
    taratura: { ...TARATURA_NEUTRA },
    assegnazione,
    confidenza: 'alta',
  };
}

/** Vincoli del dominio applicati una volta sola, così che ogni ingresso — URL
 *  compreso — produca comunque un impianto valido. */
export function normalizza(c: Partial<ConfigurazioneSimulata>): ConfigurazioneSimulata {
  const unito = { ...CONFIGURAZIONE_PREDEFINITA, ...c };
  const generatori = unito.generatori.slice(0, 4);
  return {
    ...unito,
    zone: Math.max(1, Math.min(MAX_ZONE, Math.round(unito.zone))),
    // Un impianto senza generatori non è un impianto: la cascata ne vuole almeno uno.
    generatori: generatori.length > 0 ? generatori : [CONFIGURAZIONE_PREDEFINITA.generatori[0]!],
  };
}

export function impiantoParametrico(configurazione: Partial<ConfigurazioneSimulata> = {}): Impianto {
  const c = normalizza(configurazione);
  const terzeParti = c.centralina === 'terze_parti';
  const moduloSonde = terzeParti ? 'connettore' : 'uc';
  const moduloUscite = terzeParti ? 'connettore' : 'rele';
  const limiti = { min: 16, max: 28, impostatiDa: 'Simulatore — configurazione' };

  const zone: Zona[] = STANZE.slice(0, c.zone).map((s, i) => ({
    id: `z-${s.id}`,
    nome: s.nome,
    icona: s.icona,
    ordinamento: i + 1,
    canaleSonda: `T-${s.id}`,
    canaliAttuatore: [`V-${s.id}`],
    tipoCarico: s.icona === 'bagno' ? 'RELE_ON_OFF' : 'ELETTROVALVOLA',
    abilitata: true,
    setpoint: s.setpoint,
    sovrascritta: false,
    limiti: { ...limiti },
  }));

  const canali: Canale[] = [
    ...zone.map((z) => sonda(z.canaleSonda, moduloSonde, { tipo: 'zona', zonaId: z.id })),
    ...zone.map((z) => uscita(z.canaliAttuatore[0]!, moduloUscite, { tipo: 'zona', zonaId: z.id })),
    uscita('CIRC', moduloUscite, { tipo: 'funzione', funzione: 'CIRCOLATORE' }),
  ];
  if (c.sondeCentrale) {
    canali.push(sonda('M-COLL', moduloSonde, { tipo: 'funzione', funzione: 'MANDATA_COLLETTORE' }));
    canali.push(sonda('R-COLL', moduloSonde, { tipo: 'funzione', funzione: 'RITORNO_COLLETTORE' }));
  }

  const generatori: Generatore[] = c.generatori.map((tipo, i) => {
    const id = `g-${i + 1}`;
    if (c.sondeCentrale) {
      canali.push(sonda(`M-${id}`, moduloSonde, { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: id }));
      canali.push(sonda(`R-${id}`, moduloSonde, { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: id }));
    }
    // Solo la centralina propria comanda i generatori; da un connettore in
    // sola lettura il canale di comando non esiste, e l'app lo dirà.
    if (!terzeParti) {
      canali.push(uscita(`C-${id}`, 'pwm', { tipo: 'funzione', funzione: 'COMANDO_GENERATORE', generatoreId: id }, MODULANTE[tipo] ? 'MODULAZIONE' : 'COMANDO'));
    }
    return {
      id,
      nome: NOMI_GENERATORE[tipo],
      tipo,
      // Senza sonde di centrale termica il generatore esiste ma le sue
      // temperature non sono misurate. Il dominio vuole comunque due stringhe:
      // vuote significa "nessun canale assegnato", che e' la verita'. Un id di
      // canale inesistente sarebbe peggio — un riferimento che non risolve.
      canaleMandata: c.sondeCentrale ? `M-${id}` : '',
      canaleRitorno: c.sondeCentrale ? `R-${id}` : '',
      ...(terzeParti ? {} : { canaleComando: `C-${id}` }),
      priorita: i + 1,
      modulante: MODULANTE[tipo],
    };
  });

  if (c.sanitario) {
    canali.push(sonda('ACS', moduloSonde, { tipo: 'funzione', funzione: 'ACCUMULO_SANITARIO' }));
    canali.push(uscita('DEV', moduloUscite, { tipo: 'funzione', funzione: 'VALVOLA_DEVIATRICE' }));
  }
  if (c.vmc) {
    // Su un impianto di terze parti l'uscita non passa da un nostro modulo
    // PWM — quel modulo non c'è. Il canale appartiene al connettore, come
    // tutto il resto di quell'impianto.
    canali.push(uscita('VMC', terzeParti ? moduloUscite : 'pwm', { tipo: 'funzione', funzione: 'VMC' }, 'MODULAZIONE'));
  }
  if (c.sondaEsterna) {
    canali.push(sonda('EST', moduloSonde, { tipo: 'funzione', funzione: 'TEMPERATURA_ESTERNA' }));
  }
  if (c.umidita) {
    canali.push(sonda('UMI', moduloSonde, { tipo: 'funzione', funzione: 'UMIDITA_INTERNA' }, { grandezza: 'UMIDITA', unita: '%', tipoSonda: 'ANALOGICO' }));
  }
  if (c.sondaPuffer) {
    canali.push(sonda('PUF-A', moduloSonde, { tipo: 'funzione', funzione: 'PUFFER_ALTO' }));
    canali.push(sonda('PUF-B', moduloSonde, { tipo: 'funzione', funzione: 'PUFFER_BASSO' }));
  }

  const moduli: Modulo[] = terzeParti
    ? [{ id: 'connettore', tipo: 'CONNETTORE', nome: 'Sistema di terzi', stato: 'ok', sorgenteId: 'terzi' }]
    : [
        { id: 'uc', tipo: 'UNITA_CENTRALE', nome: 'Unità centrale', stato: 'ok' },
        { id: 'rele', tipo: 'RELE_230V_16A', nome: 'Modulo relè', stato: 'ok' },
        { id: 'pwm', tipo: 'PWM_0_10V', nome: 'Uscite PWM', stato: 'ok' },
      ];

  const sorgenti: Sorgente[] = terzeParti
    ? [{ id: 'terzi', nome: 'Sistema di terzi', tipo: 'centralina', comandabile: false, descrizione: 'sorgenti.ta' }]
    : [{ id: 'emacomfort', nome: 'Centralina Ema Comfort', tipo: 'centralina', comandabile: true }];

  if (c.fotovoltaico !== 'assente') {
    moduli.push({ id: 'fv', tipo: 'CONNETTORE', nome: 'Inverter fotovoltaico', stato: 'ok', sorgenteId: 'fv' });
    sorgenti.push({ id: 'fv', nome: 'Fotovoltaico', tipo: 'fotovoltaico', comandabile: false, descrizione: 'sorgenti.fv' });
  }

  return {
    id: 'sim-configurazione',
    tenantId: 'simulatore',
    nome: 'Impianto configurato',
    indirizzo: { via: 'Via del Simulatore 1', citta: 'Configurazione', provincia: '' },
    cliente: 'Configurazione di prova',
    dataInstallazione: '2026-01-01',
    tecnicoReferente: 'Simulatore',
    centralina: terzeParti
      ? { modello: 'Sistema di terzi (connettore)', firmware: 'connettore 1.0', tipo: 'terze_parti' }
      : { modello: 'Ema Comfort UC-1 (ESP32-S3)', firmware: '0.9.2', tipo: 'emacomfort', host: 'emacomfort.local' },
    moduli,
    canali,
    zone,
    generatori,
    sorgenti,
    // `setpoint` resta un numero anche quando il sanitario è assente: il
    // dominio lo richiede, e `presente: false` è ciò che l'interfaccia legge.
    sanitario: { presente: c.sanitario, setpoint: 48, boostMinuti: 30 },
    vmc: { presente: c.vmc, modalita: c.vmc ? 'AUTO' : 'SPENTA', sogliaUmidita: 60 },
    energia:
      c.fotovoltaico === 'assente'
        ? { presente: false }
        : {
            presente: true,
            sorgenteId: 'fv',
            potenzaPiccoKw: 6,
            capacitaBatteriaKwh: c.fotovoltaico === 'con_batteria' ? 10 : 0,
          },
    parametri: structuredClone(PARAMETRI_PREDEFINITI),
    modalita: 'RISCALDAMENTO',
  };
}

// ── la configurazione nell'URL ────────────────────────────────────────────

const TIPI_VALIDI: readonly TipoGeneratore[] = ['POMPA_DI_CALORE', 'CALDAIA_GAS', 'STUFA_PELLET', 'TERMOCAMINO'];
const SIGLE: Record<TipoGeneratore, string> = {
  POMPA_DI_CALORE: 'pdc',
  CALDAIA_GAS: 'gas',
  STUFA_PELLET: 'pellet',
  TERMOCAMINO: 'camino',
};
const DA_SIGLA = Object.fromEntries(TIPI_VALIDI.map((t) => [SIGLE[t], t])) as Record<string, TipoGeneratore>;

function bool(v: string | null, predefinito: boolean): boolean {
  if (v === null) return predefinito;
  return v === '1' || v === 'true' || v === 'si';
}

/**
 * Legge una configurazione dai parametri di query. Ogni parametro assente
 * ricade sul predefinito, e un valore che non si sa leggere ricade anch'esso
 * sul predefinito invece di far fallire il caricamento: un link storto deve
 * aprire comunque un impianto, non una pagina bianca.
 */
export function daQuery(params: URLSearchParams): ConfigurazioneSimulata {
  const generatori = (params.get('generatori') ?? '')
    .split(',')
    .map((s) => DA_SIGLA[s.trim().toLowerCase()])
    .filter((t): t is TipoGeneratore => Boolean(t));
  const fv = params.get('fv');
  const zone = Number(params.get('zone'));
  return normalizza({
    centralina: params.get('centralina') === 'terze_parti' ? 'terze_parti' : CONFIGURAZIONE_PREDEFINITA.centralina,
    zone: Number.isFinite(zone) && zone > 0 ? zone : CONFIGURAZIONE_PREDEFINITA.zone,
    sanitario: bool(params.get('acs'), CONFIGURAZIONE_PREDEFINITA.sanitario),
    vmc: bool(params.get('vmc'), CONFIGURAZIONE_PREDEFINITA.vmc),
    fotovoltaico: fv === 'con_batteria' || fv === 'senza_batteria' || fv === 'assente' ? fv : CONFIGURAZIONE_PREDEFINITA.fotovoltaico,
    generatori: generatori.length > 0 ? generatori : CONFIGURAZIONE_PREDEFINITA.generatori,
    sondaEsterna: bool(params.get('esterna'), CONFIGURAZIONE_PREDEFINITA.sondaEsterna),
    umidita: bool(params.get('umidita'), CONFIGURAZIONE_PREDEFINITA.umidita),
    sondeCentrale: bool(params.get('centrale'), CONFIGURAZIONE_PREDEFINITA.sondeCentrale),
    sondaPuffer: bool(params.get('puffer'), CONFIGURAZIONE_PREDEFINITA.sondaPuffer),
  });
}

/** La configurazione come parametri di query: il link da condividere. */
export function aQuery(c: ConfigurazioneSimulata): URLSearchParams {
  return new URLSearchParams({
    centralina: c.centralina,
    zone: String(c.zone),
    acs: c.sanitario ? '1' : '0',
    vmc: c.vmc ? '1' : '0',
    fv: c.fotovoltaico,
    generatori: c.generatori.map((t) => SIGLE[t]).join(','),
    esterna: c.sondaEsterna ? '1' : '0',
    umidita: c.umidita ? '1' : '0',
    centrale: c.sondeCentrale ? '1' : '0',
    puffer: c.sondaPuffer ? '1' : '0',
  });
}
